import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  PlusCircleIcon,
  XMarkIcon,
  CalendarDaysIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/24/outline";
import { SERVER_URL } from "../config";

/* ════════════════════════════════════════════════════════════════════════════ */
const LeaveModal = ({ leaveData, setLeaveData, today, isLoading, setShowLeaveModal }) => {
  const employee = JSON.parse(localStorage.getItem("employee"));

  const [leaveTypes, setLeaveTypes] = useState([]);
  const [leaveStatus, setLeaveStatus] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Remaining leave balances
  const [remainingPlanned, setRemainingPlanned] = useState(null);
  const [remainingSick, setRemainingSick] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(true);
  const [balanceError, setBalanceError] = useState("");

  /* ── Fetch leave types ── */
  useEffect(() => {
    const fetchLeaveTypes = async () => {
      try {
        const response = await axios.get(`${SERVER_URL}/Rahul/get_leave_types.php`);
        setLeaveTypes(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Error fetching leave types:", error);
      }
    };
    fetchLeaveTypes();
  }, []);

  /* ── Fetch remaining leave balance from attendance_summary (Jan–Dec year) ── */
  const fetchBalance = async () => {
    setBalanceLoading(true);
    setBalanceError("");
    try {
      const response = await axios.get(
        `${SERVER_URL}/Rahul/attendanceAndLeaveSummary.php?employee_id=${employee.employee_id}`
      );
      const summary = response.data?.leave_summary;
      if (summary) {
        // Keys follow pattern: remaining_<leave_name_snake>_leave
        // e.g. remaining_planned_leave, remaining_sick_leave
        const plannedKey = Object.keys(summary).find((k) =>
          k.startsWith("remaining_") && k.includes("planned")
        );
        const sickKey = Object.keys(summary).find((k) =>
          k.startsWith("remaining_") && k.includes("sick")
        );
        setRemainingPlanned(plannedKey != null ? Number(summary[plannedKey]) : null);
        setRemainingSick(sickKey != null ? Number(summary[sickKey]) : null);
      } else {
        setBalanceError("Could not load leave balance.");
      }
    } catch (e) {
      console.error("Error fetching leave balance:", e);
      setBalanceError("Could not load leave balance.");
    } finally {
      setBalanceLoading(false);
    }
  };

  useEffect(() => {
    fetchBalance();
    // eslint-disable-next-line
  }, []);

  /* ── Derived: both exhausted ── */
  const bothExhausted =
    remainingPlanned !== null &&
    remainingSick !== null &&
    remainingPlanned <= 0 &&
    remainingSick <= 0;

  /* ── Helper: add N calendar days to a YYYY-MM-DD string ── */
  const addDays = (dateStr, days) => {
    const d = new Date(dateStr);
    d.setDate(d.getDate() + days);
    return d.toISOString().split("T")[0];
  };

  /* ── Derived: is the currently selected leave type "Planned"? ──
       Planned Leave → start date must be at least 30 days from today.
       Any other type → start date can be today.                      */
  const selectedLeaveTypeName =
    leaveTypes.find((t) => String(t.leave_type_id) === String(leaveData.leave_type_id))
      ?.leave_name ?? "";

  const isPlannedLeave = selectedLeaveTypeName.toLowerCase().includes("plan");

  // Earliest selectable start date
  const minStartDate = isPlannedLeave ? addDays(today, 30) : today;

  // Latest selectable end date = Dec 31 of the current calendar year
  const maxEndDate = `${new Date().getFullYear()}-12-31`;

  /* ── Leave type change: reset dates when the new type changes the min ── */
  const handleLeaveTypeChange = (e) => {
    const newTypeId = e.target.value;
    const newTypeName =
      leaveTypes.find((t) => String(t.leave_type_id) === String(newTypeId))?.leave_name ?? "";
    const newMin = newTypeName.toLowerCase().includes("plan") ? addDays(today, 30) : today;

    // If the already-chosen start_date would now be too early, wipe both dates
    const startInvalid = leaveData.start_date && leaveData.start_date < newMin;

    setLeaveData({
      ...leaveData,
      leave_type_id: newTypeId,
      start_date: startInvalid ? "" : leaveData.start_date,
      end_date:   startInvalid ? "" : leaveData.end_date,
    });
  };

  /* ── Start date change: clear end_date if it is now before the new start ── */
  const handleStartDateChange = (e) => {
    const newStart = e.target.value;
    const endInvalid = leaveData.end_date && leaveData.end_date < newStart;
    setLeaveData({
      ...leaveData,
      start_date: newStart,
      end_date: endInvalid ? "" : leaveData.end_date,
    });
  };

  /* ── Calculate working days (client-side preview, Sun + 3rd Sat off) ── */
  const calculateLeaveDays = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    let list = [];

    const isThirdSaturday = (date) => {
      const day = date.getDay();
      const dateNum = date.getDate();
      return day === 6 && dateNum >= 15 && dateNum <= 21;
    };

    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const day = d.getDay();
      if (day === 0 || isThirdSaturday(d)) list.push(0);
      else list.push(1);
    }

    return { totalDays: list.reduce((a, b) => a + b, 0), list };
  };

  /* ── Submit ── */
  const handleSubmitLeave = async () => {
    if (bothExhausted) return;
    if (!leaveData.leave_type_id || !leaveData.start_date || !leaveData.end_date) {
      alert("Please fill all required fields.");
      return;
    }

    const { totalDays, list } = calculateLeaveDays(leaveData.start_date, leaveData.end_date);
    const payload = {
      employee_id: employee.employee_id,
      leave_type_id: leaveData.leave_type_id,
      start_date: leaveData.start_date,
      end_date: leaveData.end_date,
      reason: leaveData.reason || "",
      total_days: totalDays,
      countList: list,
    };

    setSubmitting(true);
    setSubmitted(false);
    setLeaveStatus("");

    try {
      const response = await axios.post(`${SERVER_URL}/Rahul/submit_leave.php`, payload);
      const msg = response.data.message || "Leave application submitted successfully!";
      setLeaveStatus(msg);

      if (response.data.status === "success") {
        setSubmitted(true);
        setLeaveData({ leave_type_id: "", start_date: "", end_date: "", reason: "" });
        // Refresh balance after successful submission
        await fetchBalance();
      }
    } catch (e) {
      console.log(e);
      setLeaveStatus("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const isSuccess = leaveStatus.toLowerCase().includes("success");

  /* ── Balance pill helper ── */
  const BalancePill = ({ label, value, color }) => {
    const isEmpty = value !== null && value <= 0;
    return (
      <div
        className={`flex flex-col items-center px-4 py-2.5 rounded-xl border ${
          isEmpty
            ? "bg-rose-50 border-rose-200"
            : `bg-${color}-50 border-${color}-100`
        }`}
        style={{ minWidth: 100 }}
      >
        <span
          className={`text-xs font-bold uppercase tracking-wider mb-0.5 ${
            isEmpty ? "text-rose-400" : `text-${color}-400`
          }`}
        >
          {label}
        </span>
        {balanceLoading ? (
          <span className="w-5 h-5 border-2 border-slate-300 border-t-slate-500 rounded-full animate-spin mt-0.5" />
        ) : value === null ? (
          <span className="text-xs text-slate-400">—</span>
        ) : (
          <span
            className={`text-xl font-extrabold ${
              isEmpty ? "text-rose-500" : `text-${color}-600`
            }`}
          >
            {value}
          </span>
        )}
        <span
          className={`text-[10px] font-medium mt-0.5 ${
            isEmpty ? "text-rose-400" : "text-slate-400"
          }`}
        >
          {isEmpty ? "Exhausted" : "days left"}
        </span>
      </div>
    );
  };

  /* ════════════════════════════════ JSX ════════════════════════════════ */
  return (
    <>
      <style>{`
        .modal-backdrop { background: rgba(15,23,42,0.5); backdrop-filter: blur(8px); }
        .modal-card { background: linear-gradient(145deg, #ffffff 0%, #f8faff 100%); }
        .leave-input {
          width: 100%; background: #f8fafc; border: 1.5px solid #e2e8f0;
          border-radius: 10px; padding: 10px 14px; height: 42px;
          font-size: 14px; color: #334155; transition: all 0.2s;
          appearance: auto;
        }
        .leave-input:focus { outline: none; border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99,102,241,0.12); background: #fff; }
        .leave-input::placeholder { color: #94a3b8; }
        .leave-input:disabled { opacity: 0.5; cursor: not-allowed; }
        .submit-btn { background: linear-gradient(135deg, #6366f1 0%, #818cf8 100%); box-shadow: 0 4px 14px rgba(99,102,241,0.3); transition: all 0.2s; }
        .submit-btn:hover:not(:disabled) { box-shadow: 0 6px 20px rgba(99,102,241,0.4); transform: translateY(-1px); }
        .submit-btn:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
        .submit-btn-exhausted { background: linear-gradient(135deg, #f43f5e 0%, #fb7185 100%); box-shadow: 0 4px 14px rgba(244,63,94,0.25); opacity: 0.7; cursor: not-allowed; }
      `}</style>

      <div
        className="modal-backdrop fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
        onClick={(e) => e.target === e.currentTarget && setShowLeaveModal(false)}
      >
        <div
          className="modal-card w-full sm:max-w-lg rounded-t-3xl sm:rounded-2xl border border-indigo-50 overflow-hidden shadow-2xl"
          style={{ maxHeight: "95vh", overflowY: "auto" }}
        >
          {/* Drag handle (mobile) */}
          <div className="flex justify-center pt-3 pb-1 sm:hidden">
            <div className="w-10 h-1 rounded-full bg-slate-200" />
          </div>

          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center">
                <PlusCircleIcon className="w-5 h-5 text-indigo-500" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-800">Apply for Leave</h3>
                <p className="text-xs text-slate-400">Fill in the details below</p>
              </div>
            </div>
            <button
              onClick={() => setShowLeaveModal(false)}
              className="w-8 h-8 flex items-center justify-center rounded-xl bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-700 transition-all"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>

          {/* ── Leave Balance Banner ── */}
          <div className="px-6 pt-5 pb-0">
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-3">
              {new Date().getFullYear()} Remaining Balance
            </p>

            {balanceError ? (
              <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-700 font-medium mb-1">
                <ExclamationCircleIcon className="w-4 h-4 flex-shrink-0" />
                {balanceError}
              </div>
            ) : (
              <div className="flex gap-3">
                <BalancePill label="Planned" value={remainingPlanned} color="indigo" />
                <BalancePill label="Sick" value={remainingSick} color="emerald" />

                {/* Exhausted warning */}
                {bothExhausted && !balanceLoading && (
                  <div className="flex-1 flex items-center gap-2 px-4 py-2.5 rounded-xl bg-rose-50 border border-rose-200">
                    <ExclamationCircleIcon className="w-5 h-5 text-rose-400 flex-shrink-0" />
                    <p className="text-xs font-semibold text-rose-600 leading-snug">
                      All leave exhausted for {new Date().getFullYear()}. No further application allowed.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Body */}
          <div className="p-6 flex flex-col gap-5">

            {/* Leave Type */}
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                Type of Leave <span className="text-rose-400">*</span>
              </label>
              <select
                value={leaveData.leave_type_id}
                required
                disabled={bothExhausted}
                onChange={handleLeaveTypeChange}
                className="leave-input"
              >
                <option value="">Select Leave Type</option>
                {leaveTypes.map((type) => (
                  <option key={type.leave_type_id} value={type.leave_type_id}>
                    {type.leave_name}
                  </option>
                ))}
              </select>
              {/* Planned-leave notice */}
              {isPlannedLeave && (
                <p className="mt-1.5 text-[11px] text-indigo-500 font-semibold flex items-center gap-1">
                  <CalendarDaysIcon className="w-3.5 h-3.5 flex-shrink-0" />
                  Planned leave must be applied at least 30 days in advance.
                </p>
              )}
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                  <span className="flex items-center gap-1.5">
                    <CalendarDaysIcon className="w-3.5 h-3.5 text-indigo-400" />
                    Start Date <span className="text-rose-400">*</span>
                  </span>
                </label>
                <input
                  type="date"
                  value={leaveData.start_date}
                  min={minStartDate}
                  max={maxEndDate}
                  required
                  disabled={bothExhausted}
                  onChange={handleStartDateChange}
                  className="leave-input"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                  <span className="flex items-center gap-1.5">
                    <CalendarDaysIcon className="w-3.5 h-3.5 text-indigo-400" />
                    End Date <span className="text-rose-400">*</span>
                  </span>
                </label>
                <input
                  type="date"
                  value={leaveData.end_date}
                  min={leaveData.start_date || minStartDate}
                  max={maxEndDate}
                  required
                  disabled={bothExhausted || !leaveData.start_date}
                  onChange={(e) => setLeaveData({ ...leaveData, end_date: e.target.value })}
                  className="leave-input"
                />
              </div>
            </div>

            {/* Days preview */}
            {leaveData.start_date && leaveData.end_date && (
              <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-50 border border-indigo-100">
                <CalendarDaysIcon className="w-4 h-4 text-indigo-400 flex-shrink-0" />
                <span className="text-xs font-semibold text-indigo-600">
                  {(() => {
                    const { totalDays } = (() => {
                      const start = new Date(leaveData.start_date);
                      const end = new Date(leaveData.end_date);
                      let list = [];
                      const isThirdSat = (d) =>
                        d.getDay() === 6 && d.getDate() >= 15 && d.getDate() <= 21;
                      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                        const day = d.getDay();
                        list.push(day === 0 || isThirdSat(d) ? 0 : 1);
                      }
                      return { totalDays: list.reduce((a, b) => a + b, 0) };
                    })();
                    return `${totalDays} working day${totalDays !== 1 ? "s" : ""} requested`;
                  })()}
                </span>
              </div>
            )}

            {/* Reason */}
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                <span className="flex items-center gap-1.5">
                  <DocumentTextIcon className="w-3.5 h-3.5 text-indigo-400" />
                  Reason{" "}
                  <span className="normal-case text-slate-400 font-normal">(Optional)</span>
                </span>
              </label>
              <textarea
                rows="3"
                placeholder="Share any additional context…"
                value={leaveData.reason}
                disabled={bothExhausted}
                onChange={(e) => setLeaveData({ ...leaveData, reason: e.target.value })}
                className="w-full bg-slate-50 border-[1.5px] border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 focus:bg-white transition-all resize-none disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>

            {/* Status Message */}
            {leaveStatus && (
              <div
                className={`flex items-start gap-3 px-4 py-3 rounded-xl border text-sm font-medium ${
                  isSuccess
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-rose-50 text-rose-600 border-rose-200"
                }`}
              >
                {isSuccess ? (
                  <CheckCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
                ) : (
                  <ExclamationCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
                )}
                <span>{leaveStatus}</span>
              </div>
            )}

            {/* Buttons */}
            <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-1 border-t border-slate-100 mt-1">
              <button
                type="button"
                onClick={() => setShowLeaveModal(false)}
                className="w-full sm:w-auto px-5 py-2.5 rounded-xl text-sm font-semibold text-slate-600 border-[1.5px] border-slate-200 bg-white hover:bg-slate-50 hover:border-slate-300 transition-all"
              >
                {submitted ? "Close" : "Cancel"}
              </button>

              {bothExhausted ? (
                <button
                  type="button"
                  disabled
                  className="submit-btn-exhausted w-full sm:w-auto px-6 py-2.5 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2"
                >
                  <ExclamationCircleIcon className="w-4 h-4" />
                  <span>No Leave Balance</span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmitLeave}
                  disabled={submitting || isLoading}
                  className="submit-btn w-full sm:w-auto px-6 py-2.5 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2"
                >
                  {submitting || isLoading ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                      <span>Submitting…</span>
                    </>
                  ) : submitted ? (
                    <>
                      <CheckCircleIcon className="w-4 h-4" />
                      <span>Apply Another</span>
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon className="w-4 h-4" />
                      <span>Submit Application</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LeaveModal;